import React, {ComponentType} from "react";
import {ToggleState, Drawer, Box, Button, Icon, Text} from "@unibuddy/patron";
import {useTranslation} from "@unibuddy/intl";

import useAnalytics from "shared/AnalyticsNew/hooks/useAnalytics";

import MenuItem from "../MenuItem/MenuItem";
import SelectedMenuItem from "../SelectedMenuItem/SelectedMenuItem";
import IconHamburger from "../../../Icons/IconHamburger";
import {usePage} from "../../../PageProvider/PageContext";
import {useUniversity} from "../../..//UniversityProvider/UniversityProvider";
import useUniversityColor from "../../../UniversityProvider/hooks/useUniversityColor";
import {getUrls} from "../../../HeaderBar/containers/HeaderTabs/HeaderTabs";
import {MobileMenuPageMap} from "../../navigation";
import {WIDGET_PAGE} from "../../../PageProvider/PageTitles";
import {
    IconStudent,
    IconStaff,
    IconInbox,
    IconContent,
    IconHeart,
    IconSignUp,
    IconSettings,
    IconClose,
    IconLogin,
} from "../../../Icons";
import NextBestActions from "../../../NextBestActions/containers/NextBestActions";
import {useNavigationMap} from "../../hooks/useNavigationMap";
import {UniversityField} from "../../../../../types/generated";

type MobileMenuPageIndicatorProps = {
    color: string;
    toggle: () => void;
    pageIcon: ComponentType<React.ReactNode>;
    pageName: string;
    actionIcon: ComponentType<React.ReactNode>;
    buttonLabel: string;
    trackClickEvent: () => void;
};

const MobileMenuPageIndicator: React.FC<MobileMenuPageIndicatorProps> = ({
    color,
    toggle,
    pageIcon,
    pageName,
    actionIcon,
    buttonLabel,
    trackClickEvent,
}) => {
    const IconClicked = () => {
        trackClickEvent();
        toggle();
    };

    return (
        <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            borderBottomWidth={2}
            borderColor={color}
            paddingX="small"
            paddingY="medium"
        >
            <SelectedMenuItem icon={pageIcon} text={pageName} color={color} />
            <Button onClick={() => IconClicked()} clear iconOnly aria-label={buttonLabel}>
                <Icon icon={actionIcon} color="black" size="medium" />
            </Button>
        </Box>
    );
};

const MobileMenu: React.FC<any> = (props) => {
    const {page} = usePage();
    const {t} = useTranslation(["common", "recommendations", "signup"]);
    const {id, slug} = useUniversity();
    const color = useUniversityColor();
    const {trackEvent} = useAnalytics();
    const university: UniversityField = props.university;

    const {rootUrl} = getUrls(props);

    const {
        showStudents,
        showStaff,
        showRecommendations,
        showContent,
        showInbox,
        showSettings,
        showSignup,
        showLogin,
    } = useNavigationMap(university);

    if (page === WIDGET_PAGE.UNKNOWN) {
        return <Text>Loading...</Text>;
    }

    const showNba = props?.showNba ?? false;

    const trackClickEvent = (eventText: string) => {
        trackEvent(eventText, {
            universityId: id,
            universitySlug: slug,
        });
    };

    return (
        <ToggleState>
            {({state, toggle}) => (
                <Box position="relative" marginBottom="gutter">
                    <MobileMenuPageIndicator
                        color={color}
                        toggle={toggle}
                        pageIcon={MobileMenuPageMap[page].icon}
                        pageName={MobileMenuPageMap[page].text}
                        actionIcon={IconHamburger}
                        buttonLabel="iconHamburger"
                        trackClickEvent={() => trackClickEvent(`Mobile Menu Opened`)}
                    />

                    <Drawer
                        aria-haspopup="true"
                        position="fixed"
                        isOpen={state}
                        onDismiss={toggle}
                        side="left"
                        size="full"
                        closeButton={<></>}
                    >
                        <MobileMenuPageIndicator
                            color={color}
                            toggle={toggle}
                            pageIcon={MobileMenuPageMap[page].icon}
                            pageName={MobileMenuPageMap[page].text}
                            actionIcon={IconClose}
                            buttonLabel="iconClose"
                            trackClickEvent={() => trackClickEvent(`Mobile Menu Closed`)}
                        />
                        <MenuItem
                            icon={IconStudent}
                            text={t("students", "Students")}
                            to={`${rootUrl}/buddies/students`}
                            closeNavigationMenu={toggle}
                            color={color}
                            active={page === WIDGET_PAGE.WIDGET_STUDENTS}
                            trackClickEvent={() => trackClickEvent(`Clicked Students Menu Item`)}
                            show={showStudents}
                        />
                        <MenuItem
                            icon={IconStaff}
                            text={t("staff", "Staff")}
                            to={`${rootUrl}/buddies/staff`}
                            closeNavigationMenu={toggle}
                            color={color}
                            active={page === WIDGET_PAGE.WIDGET_STAFF}
                            trackClickEvent={() => trackClickEvent(`Clicked Staff Menu Item`)}
                            show={showStaff}
                        />
                        <MenuItem
                            icon={IconHeart}
                            text={t("forYou", "For You")}
                            to={`${rootUrl}/recommendations`}
                            closeNavigationMenu={toggle}
                            color={color}
                            active={page === WIDGET_PAGE.WIDGET_RECOMMENDATIONS}
                            trackClickEvent={() => trackClickEvent(`Clicked For You Menu Item`)}
                            show={showRecommendations}
                        />
                        <MenuItem
                            icon={IconContent}
                            text={t("content", "Content")}
                            to={`${rootUrl}/blog`}
                            closeNavigationMenu={toggle}
                            color={color}
                            active={page === WIDGET_PAGE.WIDGET_CONTENT}
                            trackClickEvent={() => trackClickEvent(`Clicked Content Menu Item`)}
                            show={showContent}
                        />
                        <MenuItem
                            icon={IconInbox}
                            text={t("inbox", "Inbox")}
                            to={`${rootUrl}/inbox`}
                            closeNavigationMenu={toggle}
                            color={color}
                            active={page === WIDGET_PAGE.WIDGET_INBOX}
                            trackClickEvent={() => trackClickEvent(`Clicked Inbox Menu Item`)}
                            show={showInbox}
                        />
                        <MenuItem
                            icon={IconSettings}
                            text={t("settings", "Settings")}
                            to={`${rootUrl}/settings`}
                            closeNavigationMenu={toggle}
                            color={color}
                            active={page === WIDGET_PAGE.WIDGET_SETTINGS}
                            trackClickEvent={() => trackClickEvent(`Clicked Settings Menu Item`)}
                            show={showSettings}
                        />
                        <MenuItem
                            icon={IconSignUp}
                            text={t("signup", "Sign Up")}
                            to={`${rootUrl}/auth/register`}
                            closeNavigationMenu={toggle}
                            color={color}
                            active={page === WIDGET_PAGE.WIDGET_SIGNUP}
                            trackClickEvent={() => trackClickEvent(`Clicked Sign Up Menu Item`)}
                            show={showSignup}
                        />
                        <MenuItem
                            icon={IconLogin}
                            text={t("login", "Login")}
                            to={`${rootUrl}/auth/login`}
                            closeNavigationMenu={toggle}
                            color={color}
                            active={page === WIDGET_PAGE.WIDGET_LOG_IN}
                            trackClickEvent={() => trackClickEvent(`Clicked Login Menu Item`)}
                            show={showLogin}
                        />
                    </Drawer>
                    {showNba && (
                        <Box
                            data-test-id="nba-recommendations"
                            display="flex"
                            marginTop="small"
                            paddingX={["xsmall", "small"]}
                        >
                            <NextBestActions />
                        </Box>
                    )}
                </Box>
            )}
        </ToggleState>
    );
};

export default MobileMenu;
